import { Modal } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import type { AiQuotaPaywallReason } from "@superblocksteam/shared";

import { getCurrentUser } from "legacy/selectors/usersSelectors";
import { selectOnlyOrganization } from "store/slices/organizations";
import {
  type BillingInterval,
  useGetPlanSummaryQuery,
  useCreatePortalSessionMutation,
  usePreviewInvoiceMutation,
  useUpdateSubscriptionMutation,
} from "store/slices/reduxApi/billing";
import { useListBuilderSeatsQuery } from "store/slices/reduxApi/builderSeats";
import {
  sendErrorUINotification,
  sendSuccessUINotification,
} from "utils/notification";

import { ReactComponent as UserThreeIcon } from "../../assets/icons/common/user-three.svg";
import { AssignBuilderSeatsModal } from "./AssignBuilderSeatsModal";
import { ContactAdminBillingModal } from "./ContactAdminBillingModal";
import { ManagePlanCreditPanel } from "./ManagePlanCreditPanel";
import { ManagePlanDeployPanel } from "./ManagePlanDeployPanel";
import { ManagePlanSeatPanel } from "./ManagePlanSeatPanel";
import {
  AI_CREDIT_PACK_OPTIONS,
  MOCK_DEPLOYED_APP_PRICE_MONTHLY_USD,
} from "./mockData";

export type ManagePlanFocusPanel = "seats" | "credits" | "deployed_apps";

type ManagePlanModalProps = {
  open: boolean;
  onClose: () => void;
  billingInterval?: BillingInterval;
  canManageBilling?: boolean;
  reason?: AiQuotaPaywallReason;
  /** When set, only the specified panel is shown instead of all three. */
  focusPanel?: ManagePlanFocusPanel;
  /** Called when user successfully completes an action (e.g. assigns seat). Use to navigate back to app instead of onClose. */
  onSuccess?: () => void;
};

function formatUsd(amount: number, decimals = 0) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

function formatResetDate(expiryDate: Date | string | undefined) {
  if (!expiryDate) return null;
  const date = new Date(expiryDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const REASON_BANNER_CONFIG: Partial<
  Record<AiQuotaPaywallReason, { headline: string; cta: string }>
> = {
  credit_limit_exceeded: {
    headline: "You've used all your AI Credits",
    cta: "Purchase a credit pack below to keep building.",
  },
  token_limit_exceeded: {
    headline: "You've used all your AI Credits",
    cta: "Purchase a credit pack below to keep building.",
  },
  no_seat_assigned: {
    headline: "You don't have an AI Builder license",
    cta: "Add a seat below to start building with Clark AI.",
  },
  deploy_quota_exceeded: {
    headline: "You've reached your deployed app limit",
    cta: "Add additional deployed apps below to keep deploying.",
  },
};

function getSubscriptionUpdateErrorDetail(error: unknown) {
  const data = (error as { data?: { message?: string } })?.data;
  return (
    data?.message || (error as { error?: string })?.error || "Please try again."
  );
}

export function ManagePlanModal({
  open,
  onClose,
  billingInterval: billingIntervalProp = "monthly",
  canManageBilling = true,
  reason,
  focusPanel,
  onSuccess,
}: ManagePlanModalProps) {
  const org = useSelector(selectOnlyOrganization);
  const cancelAt = org?.billing?.cancelAt
    ? new Date(org.billing.cancelAt as unknown as string | number)
    : null;
  const isPendingCancellation =
    cancelAt != null && cancelAt.getTime() > Date.now();
  const effectiveFocusPanel: ManagePlanFocusPanel | undefined =
    focusPanel ??
    (reason === "credit_limit_exceeded" || reason === "token_limit_exceeded"
      ? "credits"
      : reason === "no_seat_assigned"
        ? "seats"
        : reason === "deploy_quota_exceeded"
          ? "deployed_apps"
          : undefined);
  const reasonBanner = reason ? REASON_BANNER_CONFIG[reason] : undefined;
  const resetDate = formatResetDate(org?.billing?.expiryDate);
  const currentUser = useSelector(getCurrentUser);
  const [selectedCredits, setSelectedCredits] = useState<number | null>(null);
  type ModalView = "manage" | "confirm" | "cancel";
  const [modalView, setModalView] = useState<ModalView>("manage");
  type ConfirmSnapshot = {
    builderSeatDelta: number;
    builderSeatDeltaPrice: number;
    deployedAppDelta: number;
    deployedAppDeltaPrice: number;
    creditsDeltaPrice: number;
    creditsChanged: boolean;
    currentPlanCredits: number;
    selectedPackCredits: number;
    totalAdditionalPrice: number;
    intervalSuffix: string;
    hasImmediateCharges: boolean;
    hasDowngradeChanges: boolean;
    isDowngradeBelowUsage: boolean;
    currentUsageCredits: number;
    proratedAmount: number | null;
    proratedLoading: boolean;
  };
  const [confirmSnapshot, setConfirmSnapshot] =
    useState<ConfirmSnapshot | null>(null);
  const {
    data: builderSeatsData,
    isLoading: isBuilderSeatsLoading,
    isError: hasBuilderSeatsError,
  } = useListBuilderSeatsQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const currentBuilderSeats = builderSeatsData?.totalPurchasedSeats ?? 0;
  const currentBuilderSeatUsage = builderSeatsData?.builders.length ?? 0;
  const currentUserHasBuilderSeat = useMemo(() => {
    if (!currentUser || !builderSeatsData?.builders) {
      return null;
    }

    const normalizedEmail = currentUser.email.trim().toLowerCase();
    return builderSeatsData.builders.some(
      (builder) =>
        builder.userId === currentUser.id ||
        builder.email.trim().toLowerCase() === normalizedEmail,
    );
  }, [builderSeatsData?.builders, currentUser]);

  const showAssignModal =
    open && reason != null && currentUserHasBuilderSeat === false;
  const builderSeatCountUnavailable =
    isBuilderSeatsLoading || hasBuilderSeatsError || builderSeatsData == null;
  const [builderSeats, setBuilderSeats] = useState(currentBuilderSeats);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [updateSubscription, { isLoading }] = useUpdateSubscriptionMutation();
  const [createPortalSession] = useCreatePortalSessionMutation();
  const [previewInvoice] = usePreviewInvoiceMutation();
  const {
    data: planSummary,
    isLoading: isPlanSummaryLoading,
    isError: hasPlanSummaryError,
  } = useGetPlanSummaryQuery(undefined, {
    skip: !open || !canManageBilling,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentPlanCredits = planSummary?.currentPlanCredits ?? 0;
  const currentUsageCredits = planSummary?.currentUsageCredits ?? 0;
  const currentDeployedApps = planSummary?.deployedAppsAdditional ?? 0;
  const currentDeployedAppsUsage = planSummary?.deployedAppsUsed ?? 0;
  const deployedAppsIncluded = planSummary?.deployedAppsIncluded ?? 0;
  const deployedAppCountUnavailable =
    isPlanSummaryLoading || hasPlanSummaryError || planSummary == null;
  const effectiveBillingInterval =
    planSummary?.billingInterval ?? billingIntervalProp;
  const billingInterval = useMemo(() => {
    const billing = org?.billing as
      | { billingInterval?: string; interval?: string }
      | undefined;
    const rawInterval =
      billing?.billingInterval ?? billing?.interval ?? effectiveBillingInterval;
    return rawInterval === "annual" ? "annual" : "monthly";
  }, [effectiveBillingInterval, org?.billing]);
  const availableCreditPackOptions = useMemo(
    () =>
      AI_CREDIT_PACK_OPTIONS.filter(
        (option) => option.billingInterval === billingInterval,
      ),
    [billingInterval],
  );
  const selectedPack = useMemo(
    () =>
      availableCreditPackOptions.find(
        (option) => option.credits === selectedCredits,
      ) ?? null,
    [availableCreditPackOptions, selectedCredits],
  );
  const currentPack = useMemo(
    () =>
      availableCreditPackOptions.find(
        (option) => option.credits === currentPlanCredits,
      ) ?? null,
    [availableCreditPackOptions, currentPlanCredits],
  );
  const selectedPrice = selectedPack ? selectedPack.priceUsd : 0;
  const currentPackPrice = currentPack?.priceUsd ?? 0;
  const creditsDeltaPrice = selectedPack ? selectedPrice - currentPackPrice : 0;
  const pricePerSeatAnnual = planSummary?.pricePerSeatAnnual ?? 960;
  const pricePerSeatMonthly = planSummary?.pricePerSeatMonthly ?? 100;
  const builderSeatPrice =
    billingInterval === "annual" ? pricePerSeatAnnual : pricePerSeatMonthly;
  const intervalSuffix = billingInterval === "annual" ? "/yr" : "/mo";
  const builderSeatDelta = builderSeats - currentBuilderSeats;
  const builderSeatDeltaPrice = builderSeatDelta * builderSeatPrice;
  const deployedAppPrice =
    billingInterval === "annual"
      ? MOCK_DEPLOYED_APP_PRICE_MONTHLY_USD * 12
      : MOCK_DEPLOYED_APP_PRICE_MONTHLY_USD;
  const [deployedApps, setDeployedApps] = useState(currentDeployedApps);
  const deployedAppDelta = deployedApps - currentDeployedApps;
  const deployedAppDeltaPrice = deployedAppDelta * deployedAppPrice;
  const totalAdditionalPrice =
    builderSeatDeltaPrice + deployedAppDeltaPrice + creditsDeltaPrice;
  const creditsChanged =
    selectedCredits !== null &&
    selectedCredits !== currentPlanCredits &&
    selectedPack !== null;
  const hasPlanChanges =
    builderSeats !== currentBuilderSeats ||
    deployedApps !== currentDeployedApps ||
    creditsChanged;
  const isCreditDowngrade =
    creditsChanged &&
    selectedCredits != null &&
    selectedCredits < currentPlanCredits;
  const isDowngradeBelowUsage =
    creditsChanged &&
    selectedPack !== null &&
    selectedPack.credits < currentPlanCredits &&
    currentUsageCredits > selectedPack.credits;
  const hasImmediateCharges =
    builderSeatDelta > 0 ||
    deployedAppDelta > 0 ||
    (creditsChanged && !isCreditDowngrade);
  const hasDowngradeChanges =
    builderSeatDelta < 0 || deployedAppDelta < 0 || isCreditDowngrade;

  const [cachedProratedAmount, setCachedProratedAmount] = useState<
    number | null
  >(null);
  const [proratedLoading, setProratedLoading] = useState(false);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchPreview = useCallback(() => {
    if (!hasImmediateCharges || !open) {
      setCachedProratedAmount(null);
      setProratedLoading(false);
      return;
    }
    const changes: Array<{
      unitType: "builder_seat" | "deployed_app" | "ai_credits";
      quantity: number;
      aiCreditPackSize?: number;
    }> = [];
    if (builderSeatDelta > 0) {
      changes.push({ unitType: "builder_seat", quantity: builderSeats });
    }
    if (deployedAppDelta > 0) {
      changes.push({ unitType: "deployed_app", quantity: deployedApps });
    }
    if (creditsChanged && !isCreditDowngrade && selectedPack) {
      changes.push({
        unitType: "ai_credits",
        quantity: 1,
        aiCreditPackSize: selectedPack.credits,
      });
    }
    if (changes.length === 0) return;
    setProratedLoading(true);
    previewInvoice({ changes, billingInterval })
      .unwrap()
      .then((result) => {
        setCachedProratedAmount(result.proratedAmountDue);
        setProratedLoading(false);
      })
      .catch(() => {
        setCachedProratedAmount(null);
        setProratedLoading(false);
      });
  }, [
    hasImmediateCharges,
    open,
    builderSeats,
    builderSeatDelta,
    deployedApps,
    deployedAppDelta,
    creditsChanged,
    isCreditDowngrade,
    selectedPack,
    billingInterval,
    previewInvoice,
  ]);

  useEffect(() => {
    clearTimeout(previewTimerRef.current);
    // Don't re-fetch the preview while a subscription update is in flight;
    // the partial mutations would produce misleading proration amounts.
    if (isLoading) return;
    if (!hasImmediateCharges) {
      setCachedProratedAmount(null);
      setProratedLoading(false);
      return;
    }
    setProratedLoading(true);
    previewTimerRef.current = setTimeout(fetchPreview, 400);
    return () => clearTimeout(previewTimerRef.current);
  }, [hasImmediateCharges, fetchPreview, isLoading]);

  useEffect(() => {
    // Don't update the confirmation snapshot while the subscription
    // update is in flight — the displayed totals should stay frozen.
    if (isLoading) return;
    if (modalView === "confirm" && confirmSnapshot) {
      setConfirmSnapshot((prev) =>
        prev
          ? { ...prev, proratedAmount: cachedProratedAmount, proratedLoading }
          : prev,
      );
    }
  }, [cachedProratedAmount, proratedLoading, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) {
      return;
    }

    setBuilderSeats(currentBuilderSeats);
  }, [currentBuilderSeats, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDeployedApps(currentDeployedApps);
  }, [currentDeployedApps, open]);

  useEffect(() => {
    if (!open) return;
    if (
      currentPlanCredits > 0 &&
      availableCreditPackOptions.some(
        (option) => option.credits === currentPlanCredits,
      )
    ) {
      setSelectedCredits(currentPlanCredits);
    } else {
      setSelectedCredits(0);
    }
  }, [open, currentPlanCredits, availableCreditPackOptions]);

  useEffect(() => {
    if (
      selectedCredits != null &&
      selectedCredits !== 0 &&
      !availableCreditPackOptions.some(
        (option) => option.credits === selectedCredits,
      )
    ) {
      setSelectedCredits(null);
    }
  }, [availableCreditPackOptions, selectedCredits]);

  useEffect(() => {
    if (!isDropdownOpen) {
      setDropdownPosition(null);
      return;
    }

    const updateDropdownPosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    };

    updateDropdownPosition();

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !dropdownRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isDropdownOpen]);

  const handleSelectCredits = (credits: number | null) => {
    setSelectedCredits(credits);
    setIsDropdownOpen(false);
  };

  const performCheckout = async () => {
    let seatsUpdated = false;
    let deployedAppsUpdated = false;
    let creditsUpdated = false;

    try {
      const responses = [];

      if (builderSeats !== currentBuilderSeats) {
        const seatResponse = await updateSubscription({
          unitType: "builder_seat",
          // The API expects the new total seat quantity, not the delta.
          quantity: builderSeats,
          billingInterval,
        }).unwrap();
        seatsUpdated = true;
        responses.push(seatResponse);
      }

      if (deployedApps !== currentDeployedApps) {
        const deployResponse = await updateSubscription({
          unitType: "deployed_app",
          quantity: deployedApps,
          billingInterval,
        }).unwrap();
        deployedAppsUpdated = true;
        responses.push(deployResponse);
      }

      if (selectedPack && selectedPack.credits !== currentPlanCredits) {
        const creditsResponse = await updateSubscription({
          unitType: "ai_credits",
          quantity: 1,
          billingInterval,
          aiCreditPackSize: selectedPack.credits,
        }).unwrap();
        creditsUpdated = true;
        responses.push(creditsResponse);
      }

      if (responses.length === 0) {
        return;
      }

      sendSuccessUINotification({
        message: responses.at(-1)?.message ?? "Plan updated successfully",
      });
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error: unknown) {
      const detail = getSubscriptionUpdateErrorDetail(error);
      const parts: string[] = [];
      if (seatsUpdated && builderSeats !== currentBuilderSeats) {
        parts.push("AI Builder seats were updated");
      }
      if (deployedAppsUpdated && deployedApps !== currentDeployedApps) {
        parts.push("Deployed apps were updated");
      }
      const failedPart =
        !seatsUpdated && builderSeats !== currentBuilderSeats
          ? "AI Builder seats"
          : !deployedAppsUpdated && deployedApps !== currentDeployedApps
            ? "Deployed apps"
            : !creditsUpdated && Boolean(selectedPack)
              ? "AI credits"
              : "your plan";
      const message =
        parts.length > 0
          ? `${parts.join(" and ")}, but failed to update ${failedPart}. ${detail}`
          : `Failed to update ${failedPart}. ${detail}`;

      sendErrorUINotification({ message });
    }
  };

  if (!canManageBilling) {
    return (
      <ContactAdminBillingModal
        open={open}
        onClose={onClose}
        currentUserHasBuilderSeat={currentUserHasBuilderSeat}
        reason={reason}
      />
    );
  }

  if (open && reason != null && currentUserHasBuilderSeat === null) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={1120}
        destroyOnClose
        className="tailwind"
        modalRender={(modal) => (
          <div className="overflow-hidden rounded-lg">{modal}</div>
        )}
        bodyStyle={{ padding: 0 }}
      >
        <div className="flex h-[200px] items-center justify-center bg-white">
          <span className="text-sm text-gray-400">Loading…</span>
        </div>
      </Modal>
    );
  }

  if (showAssignModal) {
    return (
      <AssignBuilderSeatsModal
        open={open}
        onClose={onClose}
        onSuccess={onSuccess}
        showNoSeatBanner
      />
    );
  }

  const handleOpenPortalSession = async (
    flow?: "payment_method_update" | "subscription_cancel",
    event?: React.MouseEvent,
  ) => {
    try {
      const result = await createPortalSession({
        flow,
        returnTo: "/usage",
      }).unwrap();
      if (event?.metaKey || event?.ctrlKey) {
        window.open(result.portalUrl, "_blank");
      } else {
        window.location.assign(result.portalUrl);
      }
    } catch {
      sendErrorUINotification({ message: "Failed to open billing portal." });
    }
  };

  const totalCurrentPrice =
    currentBuilderSeats * builderSeatPrice +
    currentDeployedApps * deployedAppPrice +
    currentPackPrice;

  const modalWidth =
    modalView === "manage"
      ? effectiveFocusPanel
        ? 480
        : 1000
      : modalView === "confirm"
        ? 412
        : 440;

  return (
    <>
      <Modal
        open={open}
        onCancel={() => {
          onClose();
        }}
        afterClose={() => {
          setModalView("manage");
          setConfirmSnapshot(null);
        }}
        footer={null}
        width={modalWidth}
        destroyOnClose
        className="tailwind"
        closable={false}
        modalRender={(modal) => (
          <div className="overflow-hidden rounded-2xl">{modal}</div>
        )}
        bodyStyle={{ padding: 0 }}
      >
        {modalView === "manage" && (
          <div
            data-test="manage-plan-modal"
            className="flex max-h-[90vh] flex-col overflow-hidden bg-white"
          >
            {/* Header */}
            <div className="flex shrink-0 items-center gap-1.5 border-b border-[#e8eaed] px-6 py-4">
              <h2 className="!m-0 flex-1 text-[15px] font-semibold leading-5 tracking-[-0.3px] text-[#1f2633]">
                Manage your plan
              </h2>
              {!effectiveFocusPanel && isPendingCancellation && (
                <button
                  type="button"
                  onClick={(e) => {
                    handleOpenPortalSession(undefined, e);
                  }}
                  className="flex cursor-pointer items-center gap-1 rounded-[6px] border-0 bg-transparent py-1.5 pl-2 pr-3 text-xs font-medium leading-4 text-[#454d5f] transition hover:bg-[#f3f4f6]"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 8h8M8.667 4.667 12 8l-3.333 3.333" />
                  </svg>
                  Reactivate plan
                </button>
              )}
              {!effectiveFocusPanel && !isPendingCancellation ? (
                <button
                  type="button"
                  onClick={(e) => {
                    handleOpenPortalSession("payment_method_update", e);
                  }}
                  className="flex cursor-pointer items-center gap-1 rounded-[6px] border-0 bg-transparent py-1.5 pl-2 pr-3 text-xs font-medium leading-4 text-[#454d5f] transition hover:bg-[#f3f4f6]"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="4" width="12" height="8.667" rx="1.333" />
                    <path d="M2 7.333h12" />
                  </svg>
                  Update credit card
                </button>
              ) : null}
              {!effectiveFocusPanel && !isPendingCancellation ? (
                <button
                  type="button"
                  onClick={(e) => {
                    handleOpenPortalSession(undefined, e);
                  }}
                  className="flex cursor-pointer items-center gap-1 rounded-[6px] border-0 bg-transparent py-1.5 pl-2 pr-3 text-xs font-medium leading-4 text-[#454d5f] transition hover:bg-[#f3f4f6]"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9.333 2H4.667A1.333 1.333 0 0 0 3.333 3.333v9.334A1.333 1.333 0 0 0 4.667 14h6.666a1.333 1.333 0 0 0 1.334-1.333V5.333L9.333 2Z" />
                    <path d="M9.333 2v3.333h3.334" />
                  </svg>
                  See past invoices
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="flex cursor-pointer items-center justify-center rounded p-1 border-0 bg-transparent text-[#6c7689] transition hover:bg-[#f3f4f6]"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 4 4 12M4 4l8 8" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
              {isPendingCancellation && (
                <div className="rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
                  Your plan is pending cancellation. Plan changes are disabled
                  until the cancellation is reversed.
                </div>
              )}
              {reasonBanner && !isPendingCancellation ? (
                <div className="rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
                  <p className="m-0 font-medium">{reasonBanner.headline}</p>
                  <p className="m-0 mt-0.5">
                    {reasonBanner.cta}
                    {resetDate && reason !== "deploy_quota_exceeded"
                      ? ` Credits reset on ${resetDate}.`
                      : null}
                  </p>
                </div>
              ) : null}

              {/* Plan Summary Bar */}
              <div className="flex h-16 items-center gap-4 overflow-hidden rounded-lg bg-white p-4 shadow-[0_0_0_1px_rgba(31,38,51,0.12)]">
                <div className="flex h-full w-8 shrink-0 items-center justify-center rounded bg-[#f3f4f6]">
                  <UserThreeIcon className="h-4 w-4" />
                </div>
                <div className="flex flex-1 flex-col">
                  <span className="text-[13px] font-medium leading-5 text-[#1f2633]">
                    Teams plan
                  </span>
                  <span className="text-xs leading-4 text-[#6c7689]">
                    {resetDate
                      ? `Renews ${resetDate}`
                      : `${billingInterval === "annual" ? "Annual" : "Monthly"} billing`}
                  </span>
                </div>
                <div className="flex shrink-0 flex-col items-end">
                  <span className="text-[13px] font-medium leading-5 text-[#1f2633]">
                    {formatUsd(totalCurrentPrice)}
                    {intervalSuffix}
                  </span>
                  <span className="text-xs leading-4 text-[#6c7689]">
                    Current price
                  </span>
                </div>
              </div>

              {isPlanSummaryLoading ? (
                <div className="rounded-lg border border-[#e8eaed] bg-[#f9fafb] px-4 py-3 text-xs text-[#6c7689]">
                  Loading current plan summary...
                </div>
              ) : null}
              {hasPlanSummaryError ? (
                <div className="rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
                  Unable to load plan summary. Showing fallback values.
                </div>
              ) : null}

              {/* Cards */}
              <div
                className={
                  effectiveFocusPanel
                    ? "grid grid-cols-1 gap-6"
                    : "grid grid-cols-3 gap-6"
                }
              >
                {(!effectiveFocusPanel || effectiveFocusPanel === "seats") && (
                  <div className="min-w-0">
                    <ManagePlanSeatPanel
                      billingInterval={billingInterval}
                      currentBuilderSeats={currentBuilderSeats}
                      currentBuilderSeatUsage={currentBuilderSeatUsage}
                      builderSeatCountUnavailable={
                        builderSeatCountUnavailable || isPendingCancellation
                      }
                      builderSeats={builderSeats}
                      onBuilderSeatsChange={setBuilderSeats}
                      pricePerSeatAnnual={pricePerSeatAnnual}
                      pricePerSeatMonthly={pricePerSeatMonthly}
                    />
                  </div>
                )}
                {(!effectiveFocusPanel ||
                  effectiveFocusPanel === "deployed_apps") && (
                  <div className="min-w-0">
                    <ManagePlanDeployPanel
                      billingInterval={billingInterval}
                      deployedAppsIncluded={deployedAppsIncluded}
                      currentDeployedApps={currentDeployedApps}
                      currentDeployedAppsUsage={currentDeployedAppsUsage}
                      deployedAppCountUnavailable={
                        deployedAppCountUnavailable || isPendingCancellation
                      }
                      deployedApps={deployedApps}
                      onDeployedAppsChange={setDeployedApps}
                    />
                  </div>
                )}
                {(!effectiveFocusPanel ||
                  effectiveFocusPanel === "credits") && (
                  <div className="min-w-0">
                    <ManagePlanCreditPanel
                      billingInterval={billingInterval}
                      availableCreditPackOptions={availableCreditPackOptions}
                      currentPlanCredits={currentPlanCredits}
                      currentUsageCredits={currentUsageCredits}
                      selectedCredits={selectedCredits}
                      selectedPack={selectedPack}
                      isDropdownOpen={isDropdownOpen}
                      dropdownPosition={dropdownPosition}
                      triggerRef={triggerRef}
                      dropdownRef={dropdownRef}
                      disabled={isPendingCancellation}
                      onToggleDropdown={() =>
                        setIsDropdownOpen((previous) => !previous)
                      }
                      onSelectCredits={handleSelectCredits}
                    />
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="overflow-hidden rounded-lg shadow-[0_0_0_1px_rgba(31,38,51,0.12)]">
                <div className="flex flex-col gap-2 border-b border-[#e8eaed] px-4 py-3 text-xs text-[#6c7689]">
                  {builderSeatDelta !== 0 ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        {builderSeatDelta > 0 ? "+" : ""}
                        {builderSeatDelta} AI Builder{" "}
                        {Math.abs(builderSeatDelta) === 1
                          ? "License"
                          : "Licenses"}
                      </span>
                      <span>
                        {builderSeatDeltaPrice > 0 ? "+" : "−"}
                        {formatUsd(Math.abs(builderSeatDeltaPrice))}
                        {intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  {deployedAppDelta !== 0 ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        {deployedAppDelta > 0 ? "+" : ""}
                        {deployedAppDelta} Deployed App
                        {Math.abs(deployedAppDelta) === 1 ? "" : "s"}
                      </span>
                      <span>
                        {deployedAppDeltaPrice > 0 ? "+" : "−"}
                        {formatUsd(Math.abs(deployedAppDeltaPrice))}
                        {intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  {selectedPack && creditsChanged ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        AI Credits:{" "}
                        {currentPlanCredits > 0
                          ? `${currentPlanCredits.toLocaleString()} → `
                          : ""}
                        {selectedPack.credits.toLocaleString()}
                      </span>
                      <span>
                        {creditsDeltaPrice >= 0 ? "+" : "−"}
                        {formatUsd(Math.abs(creditsDeltaPrice))}
                        {intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  {!hasPlanChanges ? (
                    <div className="text-xs text-[#a4aab7]">
                      {effectiveFocusPanel === "seats"
                        ? "Adjust seats to update your plan."
                        : effectiveFocusPanel === "deployed_apps"
                          ? "Adjust deployed apps to update your plan."
                          : effectiveFocusPanel === "credits"
                            ? "Choose a credit pack to update your plan."
                            : "Adjust seats, deployed apps, and/or choose a credit pack to update your plan."}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-2 px-4 py-3">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs font-medium text-[#1f2633]">
                      Change in cost
                    </span>
                    <span className="text-[13px] font-semibold text-[#1f2633]">
                      {totalAdditionalPrice > 0
                        ? "+"
                        : totalAdditionalPrice < 0
                          ? "−"
                          : ""}
                      {formatUsd(Math.abs(totalAdditionalPrice))}
                      {intervalSuffix}
                    </span>
                  </div>
                  {hasImmediateCharges ? (
                    <p className="m-0 text-[11px] leading-4 text-[#a4aab7]">
                      {proratedLoading
                        ? "Calculating prorated charge…"
                        : cachedProratedAmount != null
                          ? `An additional prorated charge of ${formatUsd(cachedProratedAmount, 2)} will be applied immediately for the remainder of your current billing period.`
                          : "An additional prorated charge will be applied immediately for the remainder of your current billing period."}
                    </p>
                  ) : null}
                  {hasDowngradeChanges ? (
                    <p className="m-0 text-[11px] leading-4 text-[#a4aab7]">
                      This change will take effect at the start of your next
                      billing period.
                    </p>
                  ) : null}
                  {isDowngradeBelowUsage ? (
                    <p className="m-0 text-[11px] leading-4 text-amber-600">
                      You&apos;ve used{" "}
                      {Math.ceil(currentUsageCredits).toLocaleString()} credits
                      this period, which exceeds the{" "}
                      {selectedPack!.credits.toLocaleString()} credit pack. No
                      additional credits will be available until the next
                      billing cycle.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex shrink-0 items-center justify-between px-6 py-4 shadow-[0_-1px_3px_rgba(31,38,51,0.06),0_-1px_0_rgba(31,38,51,0.12)]">
              {!effectiveFocusPanel && !isPendingCancellation ? (
                <button
                  type="button"
                  onClick={() => setModalView("cancel")}
                  className="inline-flex h-8 min-w-[56px] cursor-pointer items-center justify-center rounded-[6px] border-0 bg-white px-3 text-xs font-medium leading-4 text-[#1f2633] shadow-[0_0_0_1px_rgba(31,38,51,0.12),0_1px_3px_rgba(31,38,51,0.12)] transition hover:bg-[#f9fafb]"
                >
                  Cancel plan
                </button>
              ) : (
                <div />
              )}
              <button
                type="button"
                data-test="manage-plan-checkout-btn"
                onClick={() => {
                  setConfirmSnapshot({
                    builderSeatDelta,
                    builderSeatDeltaPrice,
                    deployedAppDelta,
                    deployedAppDeltaPrice,
                    creditsDeltaPrice,
                    creditsChanged,
                    currentPlanCredits,
                    selectedPackCredits: selectedPack?.credits ?? 0,
                    totalAdditionalPrice,
                    intervalSuffix,
                    hasImmediateCharges,
                    hasDowngradeChanges,
                    isDowngradeBelowUsage,
                    currentUsageCredits,
                    proratedAmount: cachedProratedAmount,
                    proratedLoading,
                  });
                  setModalView("confirm");
                }}
                disabled={
                  isLoading ||
                  !canManageBilling ||
                  !hasPlanChanges ||
                  isPendingCancellation
                }
                className="inline-flex cursor-pointer items-center justify-center rounded-lg !bg-[#f3f4f6] px-4 py-2 text-xs font-medium leading-4 !text-[#a4aab7] !border-0 !shadow-none transition enabled:!bg-[#1f2633] enabled:!text-white enabled:hover:!bg-[#454d5f] disabled:cursor-not-allowed"
              >
                {isLoading ? "Updating..." : "Checkout →"}
              </button>
            </div>
          </div>
        )}
        {modalView === "confirm" && (
          <div className="flex flex-col bg-white">
            <div className="flex items-center gap-1.5 border-b border-[#e8eaed] px-6 py-4">
              <h3 className="!m-0 flex-1 text-[15px] font-semibold leading-5 tracking-[-0.3px] text-[#1f2633]">
                Confirm plan update
              </h3>
              <button
                type="button"
                onClick={() => onClose()}
                disabled={isLoading}
                className="flex cursor-pointer items-center justify-center rounded p-1 border-0 bg-transparent text-[#6c7689] transition hover:bg-[#f3f4f6] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 4 4 12M4 4l8 8" />
                </svg>
              </button>
            </div>
            <div className="space-y-4 px-6 pb-4 pt-5">
              {confirmSnapshot && (
                <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                  {confirmSnapshot.builderSeatDelta !== 0 ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        {confirmSnapshot.builderSeatDelta > 0 ? "+" : ""}
                        {confirmSnapshot.builderSeatDelta} AI Builder{" "}
                        {Math.abs(confirmSnapshot.builderSeatDelta) === 1
                          ? "License"
                          : "Licenses"}
                      </span>
                      <span>
                        {confirmSnapshot.builderSeatDeltaPrice > 0 ? "+" : "−"}
                        {formatUsd(
                          Math.abs(confirmSnapshot.builderSeatDeltaPrice),
                        )}
                        {confirmSnapshot.intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  {confirmSnapshot.deployedAppDelta !== 0 ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        {confirmSnapshot.deployedAppDelta > 0 ? "+" : ""}
                        {confirmSnapshot.deployedAppDelta} Deployed App
                        {Math.abs(confirmSnapshot.deployedAppDelta) === 1
                          ? ""
                          : "s"}
                      </span>
                      <span>
                        {confirmSnapshot.deployedAppDeltaPrice > 0 ? "+" : "−"}
                        {formatUsd(
                          Math.abs(confirmSnapshot.deployedAppDeltaPrice),
                        )}
                        {confirmSnapshot.intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  {confirmSnapshot.creditsChanged ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        AI Credits:{" "}
                        {confirmSnapshot.currentPlanCredits > 0
                          ? `${confirmSnapshot.currentPlanCredits.toLocaleString()} → `
                          : ""}
                        {confirmSnapshot.selectedPackCredits.toLocaleString()}
                      </span>
                      <span>
                        {confirmSnapshot.creditsDeltaPrice >= 0 ? "+" : "−"}
                        {formatUsd(Math.abs(confirmSnapshot.creditsDeltaPrice))}
                        {confirmSnapshot.intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  <div className="mt-2 border-t border-gray-200 pt-2 font-semibold text-gray-900">
                    <div className="flex items-center justify-between gap-4">
                      <span>Total change in cost</span>
                      <span>
                        {confirmSnapshot.totalAdditionalPrice > 0
                          ? "+"
                          : confirmSnapshot.totalAdditionalPrice < 0
                            ? "−"
                            : ""}
                        {formatUsd(
                          Math.abs(confirmSnapshot.totalAdditionalPrice),
                        )}
                        {confirmSnapshot.intervalSuffix}
                      </span>
                    </div>
                  </div>
                  {confirmSnapshot.hasImmediateCharges ? (
                    <p className="m-0 mt-2 text-xs font-normal text-gray-400">
                      {confirmSnapshot.proratedLoading
                        ? "Calculating prorated charge…"
                        : confirmSnapshot.proratedAmount != null
                          ? `An additional prorated charge of ${formatUsd(confirmSnapshot.proratedAmount, 2)} will be applied immediately for the remainder of your current billing period.`
                          : "An additional prorated charge will be applied immediately for the remainder of your current billing period."}
                    </p>
                  ) : null}
                  {confirmSnapshot.hasDowngradeChanges ? (
                    <p className="m-0 mt-2 text-xs font-normal text-gray-400">
                      This change will take effect at the start of your next
                      billing period.
                    </p>
                  ) : null}
                  {confirmSnapshot.isDowngradeBelowUsage ? (
                    <p className="m-0 mt-2 text-xs font-normal text-amber-600">
                      You&apos;ve used{" "}
                      {Math.ceil(
                        confirmSnapshot.currentUsageCredits,
                      ).toLocaleString()}{" "}
                      credits this period, which exceeds the{" "}
                      {confirmSnapshot.selectedPackCredits.toLocaleString()}{" "}
                      credit pack. No additional credits will be available until
                      the next billing cycle.
                    </p>
                  ) : null}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setModalView("manage")}
                  className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  data-test="manage-plan-confirm-btn"
                  onClick={() => {
                    performCheckout();
                  }}
                  disabled={isLoading}
                  className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? "Updating..." : "Confirm and continue"}
                </button>
              </div>
            </div>
          </div>
        )}
        {modalView === "cancel" && (
          <div className="flex flex-col bg-white">
            <div className="flex items-center gap-1.5 border-b border-[#e8eaed] px-6 py-4">
              <h3 className="!m-0 flex-1 text-[15px] font-semibold leading-5 tracking-[-0.3px] text-[#1f2633]">
                Are you sure you want to cancel?
              </h3>
              <button
                type="button"
                onClick={() => onClose()}
                className="flex cursor-pointer items-center justify-center rounded p-1 border-0 bg-transparent text-[#6c7689] transition hover:bg-[#f3f4f6]"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 4 4 12M4 4l8 8" />
                </svg>
              </button>
            </div>
            <div className="px-6 pb-6 pt-5">
              <p className="!m-0 text-[13px] leading-5 text-[#454d5f]">
                If you cancel
                {resetDate
                  ? `, on ${resetDate}`
                  : " at the end of your billing period"}
                :
              </p>
              <div className="mt-3 space-y-1.5 text-[13px] font-medium leading-5 text-[#1f2633]">
                <div className="flex items-start gap-2">
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                  All AI Builders lose ability to build or edit Applications
                </div>
                <div className="flex items-start gap-2">
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                  Lose access to Clark AI, integrations and security settings
                </div>
                <div className="flex items-start gap-2">
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                  All Deployed Apps will be taken offline
                </div>
                <div className="flex items-start gap-2">
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                  Any Unused AI Credits will be forfeited
                </div>
              </div>

              <div className="mt-4 rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
                Your plan will remain active
                {resetDate ? (
                  <>
                    {" "}
                    until <span className="font-medium">{resetDate}</span>
                  </>
                ) : (
                  " until the end of your billing period"
                )}
                .
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-[#e8eaed] px-6 py-4">
              <button
                type="button"
                onClick={(e) => {
                  setModalView("manage");
                  handleOpenPortalSession("subscription_cancel", e);
                }}
                className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-[#e8eaed] bg-white px-4 text-sm font-medium text-[#1f2633] shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-[#f9fafb]"
              >
                Cancel plan
              </button>
              <button
                type="button"
                onClick={() => setModalView("manage")}
                className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-[#1f2633] px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-[#1f2633]/90"
              >
                Keep my plan
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
